import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';
import Lightbox from '../../components/Lightbox';
import { breakpoints } from '../../helpers/breakpoints';
import useLocalization from '../../hooks/useLocalization';
import useTranslation from '../../hooks/useTranslation';
import { useInView } from 'react-intersection-observer';

const GallerySlider = ({ heading, images }) => {
  // const wrapperRef = useRef(null);
  const slidesRef = useRef([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [clickedImage, setClickedImage] = useState(0);
  const [translateValue, setTranslateValue] = useState(0)
  const { ref: wrapperRef, inView: wrapperInView, entry: wrapperEntry } = useInView({
    threshold: 0
  })

  const _ = useTranslation();

  // Icons
  const local = useLocalization();
  const iconChevronLeft = `${local('svgSprite')}#chevron-left`;
  const iconChevronRight = `${local('svgSprite')}#chevron-right`;

  const navigateRight = () => {
    if (activeSlide < images.length - 1) {
      setActiveSlide(activeSlide + 1);
      setClickedImage(activeSlide + 1);
      setTranslateValue(prev => prev - ((slidesRef.current[activeSlide].clientWidth / 2) + (slidesRef.current[activeSlide + 1].clientWidth / 2)))
    }
  };

  const navigateLeft = () => {
    if (activeSlide > 0) {
      setActiveSlide(activeSlide - 1);
      setClickedImage(activeSlide - 1);
      setTranslateValue(prev => prev + ((slidesRef.current[activeSlide].clientWidth / 2) + (slidesRef.current[activeSlide - 1].clientWidth / 2)))
    }
  };

  // Add Swipe Handlers
  const sliderSwipeHandlers = useSwipeable({
    onSwipedLeft: navigateRight,
    onSwipedRight: navigateLeft
  });

  const lightboxSwipeHandlers = useSwipeable({
    onSwipedLeft: navigateRight,
    onSwipedRight: navigateLeft
  });

  const showModalHandler = () => {
    setShowModal(!showModal);
  };

  const clickedImageHandler = index => {
    setClickedImage(index);
    showModalHandler();
  };

  const handleWrapperResize = () => {
    if (wrapperEntry?.target && slidesRef.current.length > 0) {
      setShowModal(false);
      setTimeout(() => {
        setActiveSlide(0);
        setTranslateValue((wrapperEntry.target.offsetWidth - slidesRef.current[0].clientWidth) / 2)
      }, 500)
    }
  };

  const handleSliderKeyDown = (e) => {
    if (wrapperInView) {
      if (e.key === 'ArrowRight') {
        navigateRight();
      } else if (e.key === 'ArrowLeft') {
        navigateLeft();
      }
    }
  };

  useEffect(() => {
    if (wrapperEntry && slidesRef && translateValue === 0) {
      setTimeout(() => {
        setTranslateValue((wrapperEntry.target.offsetWidth - slidesRef.current[0].clientWidth) / 2);
      }, 500)
    }

    const resizeObserver = new ResizeObserver((entries) => {
      handleWrapperResize();
    });

    if (wrapperEntry?.target) {
      resizeObserver.observe(wrapperEntry.target);
    }

    return () => {
      if (wrapperEntry?.target) {
        resizeObserver.unobserve(wrapperEntry.target)
      }
    };

  }, [wrapperEntry, slidesRef])

  useEffect(() => {
    window.addEventListener('keydown', handleSliderKeyDown);

    return () => {
      window.removeEventListener('keydown', handleSliderKeyDown);
    }
  }, [activeSlide, wrapperInView]);

  return (
    <>
      {heading && (
        <div className='container'>
          <h2>{heading}</h2>
        </div>
      )}
      <div className='s-gallery-slider__wrapper' style={{ '--translate-value': `${activeSlide === 0 ? 0 : translateValue}px` }} ref={wrapperRef}>
        <div className='s-gallery-slider__controls'>
          <button
            type='button'
            className='s-gallery-slider__control-left c-button c-button--icon c-button--white c-button--icon-black'
            onClick={navigateLeft}
            aria-label={_('pagination_back')}
            disabled={activeSlide === 0}
          >
            <svg viewBox='0 0 200 200'>
              <use href={iconChevronLeft}></use>
            </svg>
          </button>
          <button
            type='button'
            className='s-gallery-slider__control-right c-button c-button--icon c-button--white c-button--icon-black'
            onClick={navigateRight}
            aria-label={_('pagination_forward')}
            disabled={activeSlide === images.length - 1}
          >
            <svg viewBox='0 0 200 200'>
              <use href={iconChevronRight}></use>
            </svg>
          </button>
        </div>
        <div
          className='s-gallery-slider__slides'
          {...sliderSwipeHandlers}
        >
          {images.map((slide, index) => (
            <div
              className='s-gallery-slider__slide'
              key={index}
              ref={element => slidesRef.current[index] = element}
            >
              <LazyImage
                alt={slide.alt}
                defaultUrl={slide.sizes.medium_large}
                placeholderUrl={slide.sizes.placeholder}
                figureClass='s-gallery-slider__image'
                onClick={() => clickedImageHandler(index)}
                preload={true}
              >
                <LazySource srcSet={slide.sizes.large} mediaMin={breakpoints.md} />
                <LazySource srcSet={slide.sizes.medium_large} mediaMin={breakpoints.sm} />
              </LazyImage>
            </div>
          ))}
        </div>
        <Lightbox onClose={showModalHandler} show={showModal}>
          <div className='s-gallery-slider__lightbox' {...lightboxSwipeHandlers}>
            <LazyImage
              alt={images[clickedImage].alt}
              defaultUrl={images[clickedImage].sizes.medium_large}
              placeholderUrl={images[clickedImage].sizes.placeholder}
              figureClass='s-gallery-slider__lightbox-image'
            >
              <LazySource srcSet={images[clickedImage].sizes.large} mediaMin={breakpoints.sm} />
            </LazyImage>
            <div className="s-gallery-slider__lightbox-controls">
              <button
                type="button"
                onClick={navigateLeft}
                className='c-button c-button--icon c-button--icon-black c-button--white'
                aria-label={_('pagination_back')}
                disabled={activeSlide === 0}>
                <svg viewBox='0 0 200 200'>
                  <use href={iconChevronLeft}></use>
                </svg>
              </button>
              <button
                type="button"
                onClick={navigateRight}
                className='c-button c-button--icon c-button--icon-black c-button--white'
                aria-label={_('pagination_forward')}
                disabled={activeSlide === images.length - 1}>
                <svg viewBox='0 0 200 200'>
                  <use href={iconChevronRight}></use>
                </svg>
              </button>
            </div>
          </div>
        </Lightbox>
      </div>
    </>
  );
};

GallerySlider.propTypes = {
  heading: PropTypes.string,
  images: PropTypes.array
};

export default GallerySlider;
